/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, isNumber } from '@adobe-commerce/elsie/lib';
import { useCallback, useMemo } from 'preact/hooks';
import { ChevronDown } from '@adobe-commerce/elsie/icons';
import { Icon } from '@adobe-commerce/elsie/components/Icon';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@adobe-commerce/elsie/components/Pagination/Pagination.css';

export interface PaginationProps {
  className?: string;
  currentPage?: number;
  totalPages?: number;
  onChange?: (currentPage: number) => void;
}

export type PaginationList = {
  page: number | string;
  isActive: boolean;
  label: number | string;
};

export const Pagination: FunctionComponent<PaginationProps> = ({
  totalPages = 10,
  currentPage = 1,
  onChange,
  className,
  ...props
}) => {
  const translations = useText({
    backwardButton: 'Dropin.Pagination.backwardButton.ariaLabel',
    forwardButton: 'Dropin.Pagination.forwardButton.ariaLabel',
  });

  const handleForward = useCallback(() => {
    const nextPage = Math.min(currentPage + 1, totalPages);

    onChange?.(nextPage);
  }, [currentPage, onChange, totalPages]);

  const handleBackward = useCallback(() => {
    const prevPage = Math.max(currentPage - 1, 1);

    onChange?.(prevPage);
  }, [currentPage, onChange]);

  const handleSetPage = useCallback(
    (currentPage: number | string) => {
      if (isNumber(currentPage)) {
        onChange?.(currentPage as number);
      }
    },
    [onChange]
  );

  const createPages = useCallback(
    (currentPage: number, totalPages: number): PaginationList[] => {
      let pages: PaginationList[] = [];

      const addPageRange = (start: number, end: number) => {
        for (let i = start; i <= end; i++) {
          pages.push({ page: i, isActive: i === currentPage, label: i });
        }
      };

      if (totalPages <= 5) {
        addPageRange(1, totalPages);
      } else if (currentPage <= 2) {
        addPageRange(1, 2);
        pages.push({ page: 'ellipsis', isActive: false, label: '...' });
        addPageRange(totalPages - 1, totalPages);
      } else if (currentPage >= totalPages - 3) {
        addPageRange(totalPages - 4, totalPages);
      } else {
        addPageRange(currentPage - 1, currentPage);
        pages.push({ page: 'ellipsis', isActive: false, label: '...' });
        addPageRange(totalPages - 1, totalPages);
      }

      return pages;
    },
    []
  );

  const paginationList: PaginationList[] = useMemo(
    () => createPages(currentPage, totalPages),
    [createPages, currentPage, totalPages]
  );

  return (
    <div {...props} className={classes(['dropin-pagination', className])}>
      <button
        type="button"
        data-testid="prev-button"
        aria-label={translations.backwardButton}
        disabled={currentPage === 1}
        onClick={handleBackward}
        className={classes([
          'dropin-pagination-arrow',
          'dropin-pagination-arrow--backward',
        ])}
      >
        <Icon size="24" source={ChevronDown} />
      </button>
      <ul className="dropin-pagination_list">
        {(paginationList as PaginationList[]).map((item, index) => (
          <li
            data-testid={`dropin-pagination_list-item--${item.page}`}
            key={`${item.page}_${index}`}
            className={classes([
              'dropin-pagination_list-item',
              `dropin-pagination_list-item--${item.page}`,
              [`dropin-pagination_list-item--active`, item.isActive],
            ])}
          >
            <button
              type="button"
              data-testid={`set-page-button-${item.page}`}
              onClick={() => handleSetPage(item.page)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="next-button"
        aria-label={translations.forwardButton}
        disabled={currentPage === totalPages}
        onClick={handleForward}
        className={classes([
          'dropin-pagination-arrow',
          'dropin-pagination-arrow--forward',
        ])}
      >
        <Icon size="24" source={ChevronDown} />
      </button>
    </div>
  );
};
